import { Component, inject, input, output } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { Camera, CameraResultType } from '@capacitor/camera';
import { FilePicker, PickedFile } from '@capawesome/capacitor-file-picker';
import { of } from 'rxjs';

import { CamTranslationFiles } from '@camelot/files-basic';
import { BottomSheetTemplateBasicComponent, BottomSheetTemplateBasicParams } from '@camelot/menu';
import { TranslatePipe } from '@camelot/translation';
import { ActionButtonComponent, ActionButtonData, ButtonComponent } from '@camelot/ui';
import { FileStructure, pathToFile, pickImages } from '@camelot/utils';

export type Feature = 'take-pic' | 'upload-pic' | 'upload-file';

@Component({
  imports: [ActionButtonComponent, ButtonComponent, TranslatePipe],
  selector: 'cam-files-upload',
  styleUrls: ['./files-upload.component.scss'],
  templateUrl: './files-upload.component.html',
})
export class FilesUploadComponent {
  features = input<Feature[]>([]);

  canSelectMultipleFiles = input(false);

  showInActionButton = input(true);

  // Clé de traduction : un seul bouton « + libellé », une feuille d'actions quand il y a plusieurs fonctions.
  label = input<string | null>(null);

  filesPicked = output<FileStructure[]>();

  private readonly _bottomSheet = inject(MatBottomSheet);

  constructor() {
    CamTranslationFiles.getInstance();
  }

  get addActions(): ActionButtonData[] {
    const actionsAvailable: ActionButtonData[] = [];

    if (this._haveFeature('take-pic')) {
      actionsAvailable.push({
        callback: _ => this._takePic(),
        icon: 'image',
        label: 'files.upload.take-pic',
      });
    }

    if (this._haveFeature('upload-pic')) {
      actionsAvailable.push({
        callback: _ => this._uploadPic(),
        icon: 'folders',
        label: 'files.upload.upload-pic',
      });
    }

    if (this._haveFeature('upload-file')) {
      actionsAvailable.push({
        callback: _ => this._uploadFile(),
        icon: 'upload',
        label: 'files.upload.upload-file',
      });
    }

    return actionsAvailable;
  }

  public pick() {
    const actions = this.addActions;
    if (actions.length === 1) {
      actions[0].callback();
      return;
    }
    this._bottomSheet.open<BottomSheetTemplateBasicComponent, BottomSheetTemplateBasicParams>(
      BottomSheetTemplateBasicComponent,
      {
        data: {
          menu$: of(
            actions.map(action => ({
              action: () => {
                this._bottomSheet.dismiss();
                action.callback();
              },
              icon: action.icon as string,
              label: action.label ?? '',
            }))
          ),
          orientation: 'vertical',
        },
      }
    );
  }

  private _haveFeature(feature: Feature) {
    return this.features().includes(feature);
  }

  private async _takePic() {
    const image = await Camera.getPhoto({
      allowEditing: true,
      quality: 60,
      resultType: CameraResultType.Uri,
      saveToGallery: true,
    });

    const file = {
      file: await pathToFile(image),
      localUrl: image.webPath || null,
    };
    if (!file.file) {
      return;
    }
    this.filesPicked.emit([file]);
  }

  private async _uploadPic() {
    const pics = await pickImages();
    this.filesPicked.emit(pics);
  }

  private async _uploadFile() {
    const pickFiles = await FilePicker.pickFiles({
      multiple: this.canSelectMultipleFiles(),
      types: [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
      ],
    });

    const files = pickFiles.files
      .filter(file => !!file && !!file.blob)
      .map(file => ({ file: this._localToFile(file), localUrl: null }));

    this.filesPicked.emit(files);
  }

  private _localToFile(file: PickedFile): File {
    return new File([file.blob!], file.name, {
      type: file.mimeType,
    });
  }
}
